import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  buildPartnerGroupChannelMessageMapOptions,
  extractPartnerGroupChannelApiMessage,
  mapPartnerGroupChannelChatMessage,
} from '../components/Partner/Community/mapPartnerGroupChannel';
import {
  appendPartnerGroupChatMessageToCache,
  PARTNER_GROUP_CHANNEL_MESSAGES_PAGE_SIZE,
} from '../components/Partner/Community/partnerGroupChannelMessagesCache';
import { sendPartnerGroupChannelMessage } from '../services/partnerGroupChannel';
import { partnerGroupChannelQueryKeys } from './partner-group-channel-query-keys';

const buildSentChatMessage = (result, variables, messageMapOptions) => {
  const raw = extractPartnerGroupChannelApiMessage(result.data);
  const mapped = raw ? mapPartnerGroupChannelChatMessage(raw, 0, messageMapOptions) : null;

  if (mapped) {
    return {
      ...mapped,
      text: mapped.text || String(variables.messageText ?? '').trim(),
      isPartnerSender: true,
      source: mapped.source ?? 'partner',
      author:
        mapped.author === 'Member'
          ? messageMapOptions.senderDefaults?.fullName || mapped.author
          : mapped.author,
      avatarUrl: mapped.avatarUrl || messageMapOptions.senderDefaults?.avatarUrl || undefined,
      senderUid: mapped.senderUid || messageMapOptions.senderDefaults?.uid || null,
    };
  }

  const messageText = String(variables.messageText ?? '').trim();
  if (!messageText) {
    return null;
  }

  const senderDefaults = messageMapOptions.senderDefaults ?? {};

  return {
    id: `pending-${Date.now()}`,
    author: senderDefaults.fullName || 'Partner',
    avatarUrl: senderDefaults.avatarUrl || undefined,
    senderUid: senderDefaults.uid || null,
    source: 'partner',
    time: new Date().toISOString(),
    createdAt: new Date().toISOString(),
    text: messageText,
    attachments: [],
    isPartnerSender: true,
    reactionCounts: {},
    ownReaction: null,
    ownReactions: [],
    latestReactions: [],
    reactionChips: [],
  };
};

export const useSendPartnerGroupChatMessageMutation = (options = {}) => {
  const queryClient = useQueryClient();
  const { firebaseUid, partnerProfile, adminProfile, isAdmin } = useAuth();
  const messageMapOptions = useMemo(
    () =>
      buildPartnerGroupChannelMessageMapOptions({
        firebaseUid,
        partnerProfile: isAdmin ? adminProfile || partnerProfile : partnerProfile,
      }),
    [adminProfile, firebaseUid, isAdmin, partnerProfile],
  );
  const { onSuccess, onError, ...restOptions } = options;

  return useMutation({
    mutationFn: async ({ messageText, attachments = [], channelId } = {}) => {
      const result = await sendPartnerGroupChannelMessage({
        messageText,
        attachments,
        channelId,
      });
      if (!result.success) {
        throw new Error(result.message || 'Failed to send message');
      }
      return result;
    },
    onSuccess: async (result, variables, context) => {
      const mapped = buildSentChatMessage(result, variables, messageMapOptions);
      const channelId = variables?.channelId ? String(variables.channelId).trim() : '';
      if (mapped?.id) {
        appendPartnerGroupChatMessageToCache(queryClient, mapped, {
          limit: PARTNER_GROUP_CHANNEL_MESSAGES_PAGE_SIZE,
          ...(channelId ? { channelId } : {}),
        });
      } else {
        await queryClient.invalidateQueries({
          queryKey: [...partnerGroupChannelQueryKeys.all, 'messages'],
        });
      }
      await onSuccess?.(result, variables, context);
    },
    onError: (error, variables, context) => {
      toast.error(error?.message || 'Failed to send message');
      onError?.(error, variables, context);
    },
    ...restOptions,
  });
};

export default useSendPartnerGroupChatMessageMutation;
